import { on } from '@ember/modifier';
import { action } from '@ember/object';
import Component from '@glimmer/component';

import didInsert from 'webapp/modifiers/did-insert';
import willDestroy from 'webapp/modifiers/will-destroy';

interface Args {
	Element: HTMLDivElement;
	Blocks: { default: [] };
}

export default class MetricDetailsTopDatapointsComponent extends Component<Args> {
	private innerNode: HTMLElement | null = null;
	private resizeObserver: ResizeObserver | null = null;
	private mutationObserver: MutationObserver | null = null;

	@action
	setupView(node: HTMLElement): void {
		this.innerNode = node.querySelector('.top-datapoints__inner');

		if (this.innerNode) {
			this.resizeObserver = new ResizeObserver(() => {
				// defer to next frame to avoid "ResizeObserver loop completed with undelivered notifications"
				requestAnimationFrame(() => this.updateScrollArrows());
			});
			this.resizeObserver.observe(this.innerNode);

			this.mutationObserver = new MutationObserver(() => {
				requestAnimationFrame(() => this.updateScrollArrows());
			});
			this.mutationObserver.observe(this.innerNode, { childList: true, subtree: true });

			this.updateScrollArrows();
		}
	}

	@action
	teardownView(): void {
		this.resizeObserver?.disconnect();
		this.resizeObserver = null;
		this.mutationObserver?.disconnect();
		this.mutationObserver = null;
		this.innerNode = null;
	}

	@action
	afterScroll(): void {
		this.updateScrollArrows();
	}

	@action
	scrollPrev(): void {
		if (!this.innerNode) return;
		const scrollTo = Math.max(0, this.innerNode.scrollLeft - this.innerNode.clientWidth);
		this.innerNode.scrollTo({ left: scrollTo, behavior: 'smooth' });
	}

	@action
	scrollNext(): void {
		if (!this.innerNode) return;
		const scrollTo = this.innerNode.scrollLeft + this.innerNode.clientWidth;
		this.innerNode.scrollTo({ left: scrollTo, behavior: 'smooth' });
	}

	private updateScrollArrows(): void {
		const innerNode = this.innerNode;
		if (!innerNode) return;

		const scrollLeft = Math.round(innerNode.scrollLeft);
		const scrollOverflow = innerNode.scrollWidth - innerNode.clientWidth;

		innerNode.classList.toggle('has-arrow--prev', scrollLeft > 1);
		innerNode.classList.toggle('has-arrow--next', scrollLeft < scrollOverflow - 1);
	}

	<template>
		<div
			class="top-datapoints"
			{{didInsert this.setupView}}
			{{willDestroy this.teardownView}}
			...attributes
		>
			<div class="top-datapoints__inner" {{on "scroll" this.afterScroll}}>
				{{yield}}
			</div>
			<button
				class="top-datapoints__arrow top-datapoints__arrow--prev"
				type="button"
				aria-label="Scroll left"
				{{on "click" this.scrollPrev}}
			/>
			<button
				class="top-datapoints__arrow top-datapoints__arrow--next"
				type="button"
				aria-label="Scroll right"
				{{on "click" this.scrollNext}}
			/>
		</div>
	</template>
}
